/**
 * ModernMapContainer - Premium Map Container Component
 * 
 * A reusable, styled container for Google Maps with modern aesthetics.
 * Uses styled-components for consistent styling across the app.
 * 
 * Usage:
 *   import ModernMapContainer from '../../UI/ModernMapContainer';
 *   <ModernMapContainer height="452px">
 *     <GoogleMap ... />
 *   </ModernMapContainer>
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const Container = styled.div`
  /* Dimensions - Using !important to override Bootstrap */
  width: 100% !important;
  height: ${props => props.height || '100%'} !important;
  min-height: ${props => props.minHeight || 'auto'} !important;
  
  /* Premium Border Radius */
  border-radius: ${props => props.borderRadius || '20px'} !important;
  
  /* Elegant Floating Shadow */
  box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.1) !important;
  
  /* Subtle Border Delimitation */
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
  
  /* Prevent map overflow on curves */
  overflow: hidden !important;
  
  /* Positioning */
  position: relative !important;
  
  /* Fallback background (matches water color) */
  background-color: #a4ddf5 !important;
  
  /* Smooth transitions */
  transition: box-shadow 0.3s ease, transform 0.3s ease !important;
  
  /* Hover enhancement (optional) */
  &:hover {
    box-shadow: 0 14px 40px -5px rgba(0, 0, 0, 0.12) !important;
  }
  
  /* Responsive adjustments */
  @media screen and (max-width: 1199px) {
    border-radius: ${props => props.mobileRadius || '0px'} !important;
  }
  
  @media screen and (max-width: 991px) {
    height: ${props => props.mobileHeight || props.height || '75vh'} !important;
  }
`;

const ModernMapContainer = ({ 
  children, 
  height, 
  minHeight,
  mobileHeight,
  borderRadius,
  mobileRadius,
  className,
  style 
}) => {
  return (
    <Container
      height={height}
      minHeight={minHeight}
      mobileHeight={mobileHeight}
      borderRadius={borderRadius}
      mobileRadius={mobileRadius}
      className={className}
      style={style}
    >
      {children}
    </Container>
  );
};

ModernMapContainer.propTypes = {
  children: PropTypes.node.isRequired,
  height: PropTypes.string,
  minHeight: PropTypes.string,
  mobileHeight: PropTypes.string,
  borderRadius: PropTypes.string,
  mobileRadius: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object
};

ModernMapContainer.defaultProps = {
  height: '100%',
  borderRadius: '20px',
  mobileRadius: '0px'
};

export default ModernMapContainer;
